/**
 * The facilitator's decisions on the foresight scenarios (#830), in the
 * Työpaja card of the step that owns them:
 * - foresight_scope.v1: the scenarios started, and which go on to further
 *   work (step 2 onwards). With settings.foresight_scenario_acceptance
 *   'select' the facilitator decides each; with 'all' they all go on.
 * - scenarios.v2: which scenarios with approved tulevaisuuskuvat are
 *   published to other modules (scenario_list). With publication 'all' they
 *   are published on approval; either way one can be withdrawn here, unless
 *   the impact map is built on it.
 * The choices themselves are the modules' admin.controls (ModuleControls).
 */
import React, { useCallback, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import API_ENDPOINTS from '../../../config/api';
import { useTranslation } from '../../../hooks/useTranslation.jsx';
import useInstanceRecord from '../../../hooks/useInstanceRecord';
import useContentRefresh from '../../../hooks/useContentRefresh';
import { request } from '../../../foresight/api';
import { Muted, ErrorText } from './moduleSectionStyles';

const List = styled.ul`
  list-style: none;
  margin: 8px 0 0;
  padding: 0;
  li {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    padding: 8px 0;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
    font-size: 14px;
  }
  .name { flex: 1; min-width: 180px; }
  .state { font-size: 12px; color: rgba(255, 255, 255, 0.6); }
`;

const Choice = styled.button`
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
  border: 1px solid ${(p) => (p.$on ? '#0099ff' : 'rgba(255, 255, 255, 0.2)')};
  background: ${(p) => (p.$on ? 'rgba(0, 153, 255, 0.2)' : 'transparent')};
  color: #fff;
  &:disabled { opacity: 0.5; cursor: default; }
`;

/** @param {{ instanceId: string, moduleId: string }} props */
export default function ForesightRunsAdmin({ instanceId, moduleId }) {
  const { t } = useTranslation();
  const { instance } = useInstanceRecord(instanceId);
  const [runs, setRuns] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      setRuns((await request(API_ENDPOINTS.FORESIGHT_SCENARIOS(instanceId))).scenarios);
    } catch (e) { setError(e.message); }
  }, [instanceId]);
  useEffect(() => { load(); }, [load]);
  useContentRefresh(instanceId, ['foresight'], load, { minIntervalMs: 1500, enabled: Boolean(instanceId) });

  const acceptance = instance?.settings?.foresight_scenario_acceptance === 'select' ? 'select' : 'all';
  const isScope = moduleId === 'foresight_scope.v1';
  if (!runs) return error ? <ErrorText>{error}</ErrorText> : null;

  const decide = async (run, url, body) => {
    setBusy(run.id);
    setError(null);
    try {
      await request(url, { method: 'POST', body });
      await load();
    } catch (e) {
      setError(e.code === 'has_content' ? t('foresight.runAdmin.withdrawHasContent') : t('foresight.common.actionFailed', { error: e.message }));
    } finally { setBusy(null); }
  };

  const shown = isScope ? runs : runs.filter((r) => r.status === 'approved');
  return (
    <div>
      <strong style={{ fontSize: 14 }}>{isScope ? t('foresight.runAdmin.acceptTitle') : t('foresight.runAdmin.publishTitle')}</strong>
      {!shown.length && <Muted>{isScope ? t('foresight.runAdmin.noRuns') : t('foresight.runAdmin.noApprovedRuns')}</Muted>}
      <List>
        {shown.map((r) => (
          <li key={r.id}>
            <span className="name">
              {r.title}
              {r.subjectName && r.subjectName !== r.title && <span className="state"> · {r.subjectName}</span>}
            </span>
            {isScope && acceptance === 'all' && <span className="state">{t('foresight.runAdmin.allGoOn')}</span>}
            {isScope && acceptance === 'select' && (
              <>
                <Choice type="button" $on={r.accepted === true} disabled={busy === r.id}
                  onClick={() => decide(r, API_ENDPOINTS.FORESIGHT_SCENARIO_ACCEPTANCE(instanceId, r.id), { accepted: true })}>
                  {t('foresight.runAdmin.accept')}
                </Choice>
                <Choice type="button" $on={r.accepted === false} disabled={busy === r.id}
                  onClick={() => decide(r, API_ENDPOINTS.FORESIGHT_SCENARIO_ACCEPTANCE(instanceId, r.id), { accepted: false })}>
                  {t('foresight.runAdmin.leaveOut')}
                </Choice>
                {r.accepted === null && <span className="state">{t('foresight.runAdmin.undecided')}</span>}
              </>
            )}
            {!isScope && (
              <>
                <span className="state">{r.isActive ? t('foresight.runAdmin.published') : t('foresight.runAdmin.notPublished')}</span>
                <Choice type="button" disabled={busy === r.id}
                  onClick={() => decide(r, API_ENDPOINTS.FORESIGHT_SCENARIO_PUBLICATION(instanceId, r.id), { published: !r.isActive })}>
                  {r.isActive ? t('foresight.runAdmin.withdraw') : t('foresight.runAdmin.publish')}
                </Choice>
              </>
            )}
          </li>
        ))}
      </List>
      {error && <ErrorText>{error}</ErrorText>}
    </div>
  );
}
